<template>
  <div class="personal-center" v-loading="loading">
    <div class="pc-page-head">
      <h2 class="pc-page-title">个人中心</h2>
      <p class="pc-page-sub">账号资料与权限一览</p>
    </div>

    <div class="pc-hero">
      <div class="pc-avatar" :class="{ 'has-img': !!avatarSrc }">
        <img v-if="avatarSrc" :src="avatarSrc" alt="" />
        <span v-else>{{ userInitial }}</span>
      </div>
      <div class="pc-hero-meta">
        <div class="pc-hero-name">{{ displayName }}</div>
        <div class="pc-hero-role">{{ roleLabel }}</div>
      </div>
    </div>

    <div class="user-info">
      <div class="info-item">
        <div class="info-label">管理员名称</div>
        <div class="info-value">{{ displayName }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">账号名</div>
        <div class="info-value">{{ username }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">员工 ID</div>
        <div class="info-value">{{ employeeIdText }}</div>
      </div>
      <div class="info-item">
        <div class="info-label">密码</div>
        <div class="info-value info-value--row">
          <span>{{ maskedPassword }}</span>
          <el-button type="primary" size="small" @click="showChangePasswordDialog">修改密码</el-button>
        </div>
      </div>
      <div class="info-item info-item--block info-item--full">
        <div class="info-label">管理权限</div>
        <div class="info-value permissions">
          <template v-if="permissions.length">
            <el-tag
              v-for="permission in permissions"
              :key="permission"
              size="small"
              effect="plain"
              class="perm-tag"
            >{{ permission }}</el-tag>
          </template>
          <span v-else class="muted">—</span>
        </div>
      </div>
      <div class="info-item">
        <div class="info-label">登录时间</div>
        <div class="info-value">{{ formatLoginTime }}</div>
      </div>
    </div>

    <el-dialog title="修改密码" :visible.sync="changePasswordDialogVisible" width="400px" append-to-body>
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" show-password placeholder="请确认新密码" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="changePasswordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="changePassword">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getUserInfo, patchUserInfo } from '../utils/auth'
import { apiBase } from '../api/client'

export default {
  name: 'PersonalCenter',
  data() {
    return {
      loading: false,
      userInfo: {},
      changePasswordDialogVisible: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value !== this.passwordForm.newPassword) {
                callback(new Error('两次输入的密码不一致'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  computed: {
    maskedPassword() {
      return '********'
    },
    displayName() {
      const u = this.userInfo || {}
      return String(u.displayName || u.name || u.username || '').trim() || '—'
    },
    username() {
      return String((this.userInfo && this.userInfo.username) || '').trim() || '—'
    },
    roleLabel() {
      return String((this.userInfo && this.userInfo.roleLabel) || '').trim() || '—'
    },
    employeeIdText() {
      const id = this.userInfo && this.userInfo.employeeId
      if (id == null || id === '') return '—'
      return String(id)
    },
    permissions() {
      const list = this.userInfo && this.userInfo.permissions
      return Array.isArray(list) ? list : []
    },
    avatarSrc() {
      const raw = (this.userInfo && (this.userInfo.avatarUrl || this.userInfo.avatar_url)) || ''
      return this.resolveMediaUrl(raw)
    },
    userInitial() {
      const n = this.displayName
      if (!n || n === '—') return '管'
      return String(n).trim().slice(0, 1) || '管'
    },
    formatLoginTime() {
      if (this.userInfo && this.userInfo.loginTime) {
        return this.$formatDateTime(this.userInfo.loginTime)
      }
      return '—'
    }
  },
  mounted() {
    this.loadProfile()
  },
  methods: {
    resolveMediaUrl(url) {
      const s = String(url || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return s
      const origin = String(apiBase || '').replace(/\/api\/?$/, '')
      if (s.startsWith('/')) return origin + s
      return origin + '/' + s.replace(/^\.\//, '')
    },
    loadLocalSession() {
      this.userInfo = getUserInfo() || {}
    },
    async loadProfile() {
      this.loadLocalSession()
      this.loading = true
      try {
        const resp = await this.$api.get('/admin/profile')
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          const patch = {}
          if (d.name != null) patch.displayName = String(d.name)
          if (d.username) patch.username = String(d.username)
          if (d.employeeId != null && d.employeeId !== '') patch.employeeId = d.employeeId
          if (d.role) patch.roleLabel = String(d.role)
          if (Array.isArray(d.permissions)) patch.permissions = d.permissions
          if (d.avatar_url != null) patch.avatarUrl = String(d.avatar_url || '')
          this.userInfo = patchUserInfo(patch)
        }
      } catch (_e) {
        // 会话本地数据已足够展示
      } finally {
        this.loading = false
      }
    },
    showChangePasswordDialog() {
      this.passwordForm = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.changePasswordDialogVisible = true
      this.$nextTick(() => {
        this.$refs.passwordForm && this.$refs.passwordForm.clearValidate()
      })
    },
    changePassword() {
      this.$refs.passwordForm.validate((valid) => {
        if (!valid) return
        this.$message.warning('请联系超级管理员在「员工管理」中修改登录密码')
        this.changePasswordDialogVisible = false
      })
    }
  }
}
</script>

<style scoped>
.personal-center {
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}

.pc-page-head {
  margin-bottom: 16px;
}

.pc-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  font-family: var(--app-font);
}

.pc-page-sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: #94a3b8;
}

.pc-hero {
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
  padding: 22px 24px;
  margin-bottom: 16px;
  border-radius: 16px;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(43, 182, 115, 0.18), transparent 55%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #134e3a 100%);
  color: #fff;
  box-sizing: border-box;
}

.pc-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.35);
  background: linear-gradient(135deg, #2bb673, #0f172a);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
}

.pc-avatar.has-img {
  background: #0f172a;
  padding: 0;
}

.pc-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.pc-hero-meta {
  min-width: 0;
}

.pc-hero-name {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: var(--app-font);
}

.pc-hero-role {
  margin-top: 8px;
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(43, 182, 115, 0.22);
  border: 1px solid rgba(74, 222, 128, 0.35);
  color: #bbf7d0;
}

.user-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.info-item {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 10px 14px;
  align-items: center;
  margin: 0;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-sizing: border-box;
}

.info-item--block {
  align-items: start;
}

.info-item--full {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.info-value {
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-all;
}

.info-value--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.permissions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.perm-tag {
  margin: 0 !important;
  border-color: rgba(43, 182, 115, 0.28) !important;
  color: #166534 !important;
  background: rgba(43, 182, 115, 0.08) !important;
}

.muted {
  color: #94a3b8;
  font-weight: 500;
}

@media (max-width: 900px) {
  .user-info {
    grid-template-columns: 1fr;
  }
}
</style>
